import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { analyticsApi, ApiRequestError, type TransversalTeamDto } from "../../api/api";
import {
  AnalyticsFilterBar,
  formatScore,
  scoreToColor,
  scoreToText,
  type AnalyticsFiltersState,
} from "../../components/ui/AnalyticsFilterBar";
import { PageHeader } from "../../components/ui/PageHeader";

export function TransversalTeamPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AnalyticsFiltersState>({
    year: new Date().getFullYear(),
  });
  const [data, setData] = useState<TransversalTeamDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void analyticsApi
      .transversalTeam(filters)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          setError(err instanceof ApiRequestError ? err.message : t("analytics_load_failed"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, t]);

  const grouped = useMemo(() => {
    const catMap = new Map<string, NonNullable<TransversalTeamDto["skills"]>>();
    for (const s of data?.skills ?? []) {
      const cat = s.skill.macroProcess;
      const list = catMap.get(cat) ?? [];
      list.push(s);
      catMap.set(cat, list);
    }
    return [...catMap.entries()].map(([category, skills]) => ({ category, skills }));
  }, [data]);

  const users = data?.users ?? [];
  const threshold = data?.threshold ?? 2;

  return (
    <div className="sm-page">
      <PageHeader title={t("transversal_team")} />
      <AnalyticsFilterBar filters={filters} onChange={setFilters} />
      {error ? <p className="sm-error">{error}</p> : null}
      {loading ? (
        <p className="sm-muted">{t("loading")}</p>
      ) : !data || users.length === 0 ? (
        <div className="sm-card sm-card-pad sm-muted">{t("no_data")}</div>
      ) : (
        <div className="sm-card sm-card-pad sm-heatmap-wrap">
          <div className="sm-analytics-meta">
            <span className="sm-muted">
              {data.skills.length} · {users.length} experts
            </span>
            <div className="sm-score-legend">
              {[0, 1, 2, 3, 4].map((s) => (
                <span key={s}>
                  <i style={{ background: scoreToColor(s) }} />
                  {s}
                </span>
              ))}
              <span className="sm-muted" style={{ marginLeft: 8 }}>
                {t("target_threshold")}: <strong>{threshold}</strong>
              </span>
            </div>
          </div>
          <div className="sm-table-wrap">
            <table className="sm-heatmap-table sm-heatmap-tv">
              <thead>
                <tr>
                  <th>{t("transversal_skills")}</th>
                  <th>Avg</th>
                  {users.map((u) => (
                    <th key={u.id} title={u.name}>
                      {u.name.split(" ")[0]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grouped.map(({ category, skills }) => (
                  <Fragment key={category}>
                    <tr>
                      <td colSpan={2 + users.length} className="sm-tv-cat">
                        {category}
                      </td>
                    </tr>
                    {skills.map((s) => {
                      const label =
                        s.skill.label ||
                        s.skill.task ||
                        s.skill.subProcess ||
                        s.skill.businessProcess;
                      const above = s.scores.filter(
                        (sc) => sc.score !== null && sc.score >= threshold
                      ).length;
                      const total = s.scores.filter((sc) => sc.score !== null).length;
                      const isCritical = total > 0 && above === 0;
                      const isSpof = above === 1;
                      return (
                        <tr key={s.skill.id}>
                          <td>
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              <span title={`${s.skill.businessProcess} · ${s.skill.subProcess ?? ""}`}>
                                {label}
                              </span>
                              {isCritical ? (
                                <span className="sm-badge-pill sm-badge-red">{t("critical")}</span>
                              ) : null}
                              {isSpof && !isCritical ? (
                                <span className="sm-badge-pill sm-badge-yellow">{t("spof_risk")}</span>
                              ) : null}
                            </div>
                          </td>
                          <td className="sm-heat-td">
                            <span
                              className="sm-heat-cell"
                              style={{
                                background: scoreToColor(s.avg),
                                color: scoreToText(s.avg),
                              }}
                            >
                              {formatScore(s.avg)}
                            </span>
                          </td>
                          {s.scores.map((sc, j) => (
                            <td key={j} className="sm-heat-td">
                              <span
                                className={[
                                  "sm-heat-cell",
                                  sc.score === null ? "is-empty" : "",
                                  sc.score !== null && sc.score < threshold ? "is-below" : "",
                                ]
                                  .filter(Boolean)
                                  .join(" ")}
                                style={{
                                  background: scoreToColor(sc.score),
                                  color: scoreToText(sc.score),
                                }}
                                title={`${users[j]?.name}: ${formatScore(sc.score)}`}
                              >
                                {formatScore(sc.score)}
                              </span>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
